
import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Appointment, AppointmentType, User, UserRole, Lead } from '../types';
import { api } from '../services/apiService';

// --- INTERFACES ---
interface ContextMenuState {
    x: number;
    y: number;
    type: 'empty' | 'appointment';
    data: any;
}

// --- COMPOSANT MODALE ---
interface AppointmentModalProps {
    appt: Partial<Appointment> | null;
    isNew: boolean;
    leads: Lead[];
    onClose: () => void;
    onSave: (e: React.FormEvent) => void;
    onDelete: (id: string) => void;
    onChange: (updates: Partial<Appointment>) => void;
}

const AppointmentModal: React.FC<AppointmentModalProps> = ({ appt, isNew, leads, onClose, onSave, onDelete, onChange }) => {
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    useEffect(() => {
        setShowDeleteConfirm(false);
    }, [appt?.id]);

    if (!appt) return null;

    const startD = appt.start ? new Date(appt.start) : new Date();
    const endD = appt.end ? new Date(appt.end) : new Date(startD.getTime() + 3600000);
    
    const dateStr = startD.toISOString().split('T')[0];
    const timeStartStr = startD.toTimeString().slice(0, 5);
    const timeEndStr = endD.toTimeString().slice(0, 5);

    const content = (
        <div className="orion-modal-backdrop fixed inset-0 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in" onMouseDown={(e) => e.stopPropagation()}>
            <div className="orion-modal-panel w-full max-w-md rounded-3xl p-8 shadow-2xl border animate-in zoom-in-95" onMouseDown={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-black text-slate-800 dark:text-white">{isNew ? 'Nouveau Rendez-vous' : 'Modifier Rendez-vous'}</h3>
                    {!isNew && appt.id && (
                        !showDeleteConfirm ? (
                            <button 
                                type="button" 
                                onClick={(e) => { e.preventDefault(); setShowDeleteConfirm(true); }} 
                                className="text-rose-400 hover:bg-rose-500/15 p-2 rounded-xl transition-colors group"
                                title="Supprimer"
                            >
                                <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                        ) : (
                            <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-2">
                                <span className="text-[10px] font-black text-rose-600 uppercase">Sûr ?</span>
                                <button 
                                    type="button" 
                                    onClick={(e) => { e.preventDefault(); onDelete(appt.id!); }} 
                                    className="bg-rose-500 text-white px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-rose-600 shadow-sm"
                                >
                                    Oui
                                </button>
                                <button 
                                    type="button" 
                                    onClick={(e) => { e.preventDefault(); setShowDeleteConfirm(false); }} 
                                    className="orion-modal-subcard text-slate-300 px-3 py-1.5 rounded-lg text-xs font-bold"
                                >
                                    Non
                                </button>
                            </div>
                        )
                    )}
                </div>
                <form onSubmit={onSave} className="space-y-4">
                    {isNew && (
                        <div>
                            <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Prospect concerné</label>
                            <select 
                                required
                                value={appt.leadId || ''} 
                                onChange={e => onChange({ leadId: e.target.value })}
                                className="orion-modal-input w-full rounded-xl px-4 py-3 font-bold text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="">-- Sélectionner un prospect --</option>
                                {leads.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                            </select>
                        </div>
                    )}
                    
                    <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Titre</label>
                        <input 
                            type="text" 
                            required
                            autoFocus
                            value={appt.title || ''} 
                            onChange={e => onChange({ title: e.target.value })} 
                            className="orion-modal-input w-full rounded-xl px-4 py-3 font-bold text-sm outline-none focus:ring-2 focus:ring-indigo-500" 
                            placeholder="Objet du RDV"
                        />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                         <div>
                            <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Date</label>
                            <input 
                                type="date" 
                                value={dateStr}
                                onChange={e => {
                                    const newDate = e.target.value;
                                    if(!newDate) return;
                                    const s = appt.start ? new Date(appt.start) : new Date();
                                    const e_date = appt.end ? new Date(appt.end) : new Date();
                                    const newStart = new Date(`${newDate}T${s.toTimeString().slice(0,5)}`);
                                    const newEnd = new Date(`${newDate}T${e_date.toTimeString().slice(0,5)}`);
                                    onChange({ start: newStart.toISOString(), end: newEnd.toISOString() });
                                }}
                                className="orion-modal-input w-full rounded-xl px-3 py-3 font-bold text-sm outline-none"
                            />
                         </div>
                         <div>
                            <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Type</label>
                            <select 
                                value={appt.type || AppointmentType.PHONE}
                                onChange={e => onChange({ type: e.target.value as AppointmentType })}
                                className="orion-modal-input w-full rounded-xl px-3 py-3 font-bold text-sm outline-none"
                            >
                                <option value={AppointmentType.PHONE}>Téléphone</option>
                                <option value={AppointmentType.VIDEO}>Visio</option>
                                <option value={AppointmentType.PHYSICAL}>Physique</option>
                            </select>
                         </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Début</label>
                            <input 
                                type="time" 
                                value={timeStartStr}
                                onChange={e => {
                                    const datePart = (appt.start || new Date().toISOString()).split('T')[0];
                                    const newStart = new Date(`${datePart}T${e.target.value}`);
                                    onChange({ start: newStart.toISOString() });
                                }}
                                className="orion-modal-input w-full rounded-xl px-3 py-3 font-bold text-sm outline-none"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Fin</label>
                            <input 
                                type="time" 
                                value={timeEndStr}
                                onChange={e => {
                                    const datePart = (appt.end || new Date().toISOString()).split('T')[0];
                                    const newEnd = new Date(`${datePart}T${e.target.value}`);
                                    onChange({ end: newEnd.toISOString() });
                                }}
                                className="orion-modal-input w-full rounded-xl px-3 py-3 font-bold text-sm outline-none"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Notes / Description</label>
                        <textarea 
                            value={appt.description || ''} 
                            onChange={e => onChange({ description: e.target.value })} 
                            placeholder="Détails du rendez-vous..."
                            className="orion-modal-input w-full rounded-xl px-4 py-3 font-medium text-sm outline-none h-24 resize-none" 
                        />
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button type="button" onClick={onClose} className="orion-modal-ghost-btn flex-1 py-3 font-bold text-xs">Annuler</button>
                        <button type="submit" className="orion-modal-primary-btn flex-1 text-white rounded-xl py-3 font-bold text-xs shadow-lg">
                            {isNew ? 'Créer' : 'Enregistrer'}
                        </button>
                    </div>
                </form>
            </div>
            <div className="absolute inset-0 z-[-1]" onClick={onClose}></div>
        </div>
    );
    
    return createPortal(content, document.body);
};


// --- COMPOSANT MENU CONTEXTUEL ---
interface ContextMenuProps {
    x: number;
    y: number;
    type: 'empty' | 'appointment';
    data: any;
    onClose: () => void;
    onNew: (date: Date) => void;
    onEdit: (appt: Appointment) => void;
    onDelete: (id: string) => void;
}

const ContextMenu: React.FC<ContextMenuProps> = ({ x, y, type, data, onClose, onNew, onEdit, onDelete }) => {
    const [confirmDelete, setConfirmDelete] = useState(false);
    
    const style: React.CSSProperties = { top: y, left: x };

    if (x > window.innerWidth - 200) style.left = x - 180;
    if (y > window.innerHeight - 150) style.top = y - 130;

    return createPortal(
        <div 
            className="fixed inset-0 z-[10000]" 
            onClick={onClose} 
            onContextMenu={(e) => { e.preventDefault(); onClose(); }}
        >
            <div 
                style={style} 
                onClick={(e) => e.stopPropagation()} 
                className="orion-context-menu fixed border rounded-xl shadow-2xl py-1 w-44 animate-in fade-in duration-100"
            >
                {type === 'empty' ? (
                    <button 
                        onClick={(e) => { e.stopPropagation(); onClose(); onNew(data); }}
                        className="orion-context-item w-full text-left px-4 py-2.5 text-xs font-bold flex items-center gap-2"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                        Nouveau RDV
                    </button>
                ) : (
                    <>
                        <button 
                            onClick={(e) => { e.stopPropagation(); onClose(); onEdit(data); }}
                            className="orion-context-item w-full text-left px-4 py-2.5 text-xs font-bold flex items-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                            Modifier
                        </button>
                        
                        {!confirmDelete ? (
                            <button 
                                onClick={(e) => { 
                                    e.stopPropagation(); 
                                    setConfirmDelete(true);
                                }}
                                className="orion-context-item-danger w-full text-left px-4 py-2.5 text-xs font-bold flex items-center gap-2 border-t border-slate-100 dark:border-slate-800"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                Supprimer
                            </button>
                        ) : (
                             <button 
                                onClick={(e) => { 
                                    e.stopPropagation(); 
                                    onClose(); // Ferme le menu
                                    if(data && data.id) {
                                        onDelete(data.id); // Supprime directement
                                    }
                                }}
                                className="orion-context-item-danger-confirm w-full text-left px-4 py-2.5 text-xs font-black text-white flex items-center gap-2 border-t animate-in fade-in"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                                Confirmer ?
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>,
        document.body
    );
};


// --- COMPOSANT PRINCIPAL ---
interface CalendarViewProps {
  currentUser: User;
  leads: Lead[];
  companyUsers: User[];
}

const CalendarView: React.FC<CalendarViewProps> = ({ currentUser, leads, companyUsers }) => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [view, setView] = useState<'day' | 'week' | 'month'>(window.innerWidth < 768 ? 'day' : 'week');
  const [selectedAgentId, setSelectedAgentId] = useState<string>(currentUser.id);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());

  const [editingAppt, setEditingAppt] = useState<Partial<Appointment> | null>(null);
  const [isNewAppt, setIsNewAppt] = useState(false);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  
  // --- DRAG AND DROP STATE ---
  const [draggedAppt, setDraggedAppt] = useState<Appointment | null>(null);

  const fetchAppts = async () => {
      const data = await api.getAppointments({ userId: selectedAgentId });
      setAppointments(data);
      setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    fetchAppts();
  }, [selectedAgentId]);

  const getApptColor = (type: AppointmentType) => {
    switch(type) {
      case AppointmentType.VIDEO: return 'orion-appt-video text-blue-100 border-blue-300/30';
      case AppointmentType.PHYSICAL: return 'orion-appt-physical text-emerald-100 border-emerald-300/30';
      default: return 'orion-appt-phone text-amber-100 border-amber-300/30';
    }
  };

  const calendarDays = useMemo(() => {
    const days = [];
    const start = new Date(currentDate);

    if (view === 'day') {
        days.push(new Date(start));
    } else if (view === 'week') {
        const day = start.getDay();
        const diff = start.getDate() - day + (day === 0 ? -6 : 1);
        start.setDate(diff);
        for (let i = 0; i < 7; i++) {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            days.push(d);
        }
    } else if (view === 'month') {
        start.setDate(1);
        const dayOfWeek = start.getDay();
        const offset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        start.setDate(start.getDate() - offset);
        for (let i = 0; i < 42; i++) {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            days.push(d);
        }
    }
    return days;
  }, [currentDate, view]);

  const hours = Array.from({ length: 13 }, (_, i) => i + 8);

  // --- HANDLERS ---
  const handleContextMenu = (e: React.MouseEvent, type: 'empty' | 'appointment', data: any) => {
      e.preventDefault();
      e.stopPropagation();
      setContextMenu({
          x: e.clientX,
          y: e.clientY,
          type,
          data
      });
  };

  // --- DRAG & DROP LOGIC ---
  const handleDragStart = (e: React.DragEvent, appt: Appointment) => {
      e.stopPropagation();
      setDraggedAppt(appt);
      // Pour éviter l'effet "fantôme" trop opaque
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', appt.id);
  };

  const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetDate: Date, targetHour?: number) => {
      e.preventDefault();
      if (!draggedAppt) return;

      const duration = new Date(draggedAppt.end).getTime() - new Date(draggedAppt.start).getTime();
      const originalStart = new Date(draggedAppt.start);
      
      const newStart = new Date(targetDate);
      
      if (targetHour !== undefined) {
          // Vue Semaine/Jour : On applique l'heure précise
          newStart.setHours(targetHour);
          newStart.setMinutes(originalStart.getMinutes()); // On garde les minutes d'origine
      } else {
          // Vue Mois : On garde l'heure d'origine
          newStart.setHours(originalStart.getHours());
          newStart.setMinutes(originalStart.getMinutes());
      }
      
      const newEnd = new Date(newStart.getTime() + duration);
      
      // Optimistic Update
      const updatedAppt = { ...draggedAppt, start: newStart.toISOString(), end: newEnd.toISOString() };
      setAppointments(prev => prev.map(a => a.id === updatedAppt.id ? updatedAppt : a));
      setDraggedAppt(null);

      // API Call
      await api.updateAppointment(updatedAppt.id, { start: updatedAppt.start, end: updatedAppt.end });
  };

  const openNewApptModal = (startDate?: Date) => {
      const start = startDate || new Date();
      start.setSeconds(0);
      start.setMilliseconds(0);
      const end = new Date(start.getTime() + 60 * 60 * 1000);

      setEditingAppt({
          title: '',
          description: '',
          start: start.toISOString(),
          end: end.toISOString(),
          type: AppointmentType.PHONE,
          leadId: '',
          userId: selectedAgentId
      });
      setIsNewAppt(true);
      setContextMenu(null);
  };

  const openEditModal = (appt: Appointment) => {
      setEditingAppt(appt);
      setIsNewAppt(false);
      setContextMenu(null);
  };

  const handleDelete = async (id: string) => {
      if (!id) return;
      setAppointments(prev => prev.filter(a => a.id !== id));
      setEditingAppt(null);
      setContextMenu(null);
      await api.deleteAppointment(id);
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
      e.preventDefault();
      if(!editingAppt || !editingAppt.start || !editingAppt.end || !editingAppt.title) return;
      
      if(isNewAppt && !editingAppt.leadId) {
          alert("Veuillez sélectionner un prospect.");
          return;
      }

      if (isNewAppt) {
          const newAppt = await api.addAppointment(editingAppt as any);
          setAppointments([...appointments, newAppt]);
      } else {
          setAppointments(prev => prev.map(a => a.id === editingAppt.id ? { ...a, ...editingAppt } as Appointment : a));
          await api.updateAppointment(editingAppt.id!, editingAppt);
      }
      setEditingAppt(null);
  };

  const navigate = (offset: number) => {
      const newDate = new Date(currentDate);
      if (view === 'month') newDate.setMonth(newDate.getMonth() + offset);
      else if (view === 'week') newDate.setDate(newDate.getDate() + (offset * 7));
      else newDate.setDate(newDate.getDate() + offset);
      setCurrentDate(newDate);
  };

  const formatDateTitle = (date: Date) => {
      return new Intl.DateTimeFormat('fr-FR', { weekday: 'short', day: 'numeric' }).format(date);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500" onContextMenu={(e) => e.preventDefault()}>
      
      <AppointmentModal 
          appt={editingAppt}
          isNew={isNewAppt}
          leads={leads}
          onClose={() => setEditingAppt(null)}
          onSave={handleSaveEdit}
          onDelete={handleDelete}
          onChange={(updates) => setEditingAppt(prev => prev ? { ...prev, ...updates } : null)}
      />

      {contextMenu && (
          <ContextMenu 
             x={contextMenu.x}
             y={contextMenu.y}
             type={contextMenu.type}
             data={contextMenu.data}
             onClose={() => setContextMenu(null)}
             onNew={openNewApptModal}
             onEdit={openEditModal}
             onDelete={handleDelete}
          />
      )}
      
      <div className="orion-toolbar flex flex-col xl:flex-row justify-between items-center gap-3 p-3 rounded-xl border flex-shrink-0">
        {/* Gauche : navigation mois */}
        <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-[rgba(100,140,210,0.08)] rounded-lg p-1">
                <button type="button" onClick={() => navigate(-1)} className="p-2 hover:bg-[rgba(100,140,210,0.15)] rounded-md transition-colors text-[#7f93bc] hover:text-[#c8daf5]">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" /></svg>
                </button>
                <span className="px-4 text-[13px] font-black text-[#c8daf5] min-w-[150px] text-center capitalize tracking-wide">
                    {view === 'day'
                        ? new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long' }).format(currentDate)
                        : new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' }).format(currentDate)
                    }
                </span>
                <button type="button" onClick={() => navigate(1)} className="p-2 hover:bg-[rgba(100,140,210,0.15)] rounded-md transition-colors text-[#7f93bc] hover:text-[#c8daf5]">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
                </button>
            </div>
            <button type="button" onClick={() => setCurrentDate(new Date())} className="text-[11px] font-bold text-[#7f93bc] hover:text-[#11c5d6] hover:bg-[rgba(17,197,214,0.08)] px-3 py-2 rounded-lg transition-all">
                Aujourd'hui
            </button>
        </div>

        {/* Droite : actions */}
        <div className="flex items-center gap-2">
            {/* Sélecteur de vue */}
            <div className="flex bg-[rgba(100,140,210,0.08)] p-1 rounded-lg">
                {(['day', 'week', 'month'] as const).map(v => (
                    <button
                        type="button"
                        key={v}
                        onClick={() => setView(v)}
                        className={`px-4 py-1.5 rounded-md text-[11px] font-black capitalize transition-all ${view === v ? 'bg-[rgba(17,197,214,0.2)] text-[#11c5d6]' : 'text-[#546a96] hover:text-[#7f93bc]'}`}
                    >
                        {v === 'day' ? 'Jour' : v === 'week' ? 'Semaine' : 'Mois'}
                    </button>
                ))}
            </div>

            {/* Sélecteur d'agent (admin) */}
            {currentUser.role === UserRole.COMPANY_ADMIN && (
                <div className="border-l border-[rgba(100,140,210,0.2)] pl-3 ml-1">
                    <select
                        value={selectedAgentId}
                        onChange={(e) => setSelectedAgentId(e.target.value)}
                        className="bg-[rgba(100,140,210,0.08)] border border-[rgba(100,140,210,0.15)] rounded-lg px-3 py-2 text-[11px] font-bold text-[#7f93bc] outline-none cursor-pointer hover:border-[rgba(100,140,210,0.3)] transition-all"
                    >
                        {companyUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                </div>
            )}

            {/* Bouton Nouveau RDV */}
            <button
                type="button"
                onClick={() => openNewApptModal()}
                className="orion-primary-btn flex items-center gap-2 px-4 py-2 rounded-lg text-[11px] font-black"
            >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" /></svg>
                Nouveau RDV
            </button>
        </div>
      </div>

      <div className="orion-widget rounded-xl overflow-hidden select-none min-h-[600px] flex flex-col">
        {loading ? (
          <div className="flex-1 flex items-center justify-center text-[#546a96] animate-pulse text-xs font-bold">Chargement...</div>
        ) : (
          <>
            <div className={`orion-calendar-header grid ${view === 'month' ? 'grid-cols-7' : view === 'day' ? 'grid-cols-[80px_1fr]' : 'grid-cols-[80px_repeat(7,1fr)]'}`}>
                {view !== 'month' && <div className="p-4 border-r orion-calendar-header-border"></div>}
                {(view === 'month' ? ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'] : calendarDays).map((day, i) => (
                  <div key={i} className={`orion-calendar-header-cell ${(day instanceof Date && day.toDateString() === new Date().toDateString()) ? 'orion-calendar-header-today' : ''}`}>
                    <div className="orion-calendar-header-text">
                        {typeof day === 'string' ? day : formatDateTitle(day)}
                    </div>
                  </div>
                ))}
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar relative orion-calendar-body">
                {view === 'month' && (
                     <div className="grid grid-cols-7 auto-rows-[120px]">
                        {calendarDays.map((date, i) => {
                            const isCurrentMonth = date.getMonth() === currentDate.getMonth();
                            const isToday = date.toDateString() === new Date().toDateString();
                            const dayAppts = appointments.filter(a => new Date(a.start).toDateString() === date.toDateString());

                            return (
                                <div 
                                    key={i} 
                                    onContextMenu={(e) => handleContextMenu(e, 'empty', date)}
                                    onDragOver={handleDragOver}
                                    onDrop={(e) => handleDrop(e, date)}
                                    className={`orion-calendar-cell-hover border-b border-r border-slate-100 p-2 transition-colors ${!isCurrentMonth ? 'bg-slate-50/30 text-slate-300' : ''} ${isToday ? 'bg-indigo-50/10' : ''}`}
                                >
                                    <div className={`text-right text-xs font-bold mb-2 ${isToday ? 'text-indigo-600' : 'text-slate-400'}`}>
                                        {date.getDate()}
                                    </div>
                                    <div className="space-y-1 overflow-y-auto max-h-[80px] custom-scrollbar">
                                        {dayAppts.map(appt => (
                                            <div 
                                                key={appt.id}
                                                draggable
                                                onDragStart={(e) => handleDragStart(e, appt)}
                                                onClick={(e) => { e.stopPropagation(); openEditModal(appt); }}
                                                onContextMenu={(e) => handleContextMenu(e, 'appointment', appt)}
                                                className={`text-[8px] px-1 py-0.5 rounded-md truncate cursor-pointer hover:opacity-85 active:cursor-grabbing leading-tight ${getApptColor(appt.type)}`}
                                                title={appt.title}
                                            >
                                                {new Date(appt.start).getHours()}h {appt.title}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                     </div>
                )}

                {(view === 'week' || view === 'day') && (
                     <div className="relative min-w-[600px]">
                        {hours.map(hour => (
                          <div key={hour} className={`orion-calendar-row grid border-b border-slate-50 h-24 relative ${view === 'day' ? 'grid-cols-[80px_1fr]' : 'grid-cols-[80px_repeat(7,1fr)]'}`}>
                            <div className="orion-calendar-time-col p-3 text-xs font-bold text-slate-400 text-right border-r border-slate-100 pr-4 sticky left-0 bg-white z-10">
                                {hour}:00
                            </div>
                            {calendarDays.map((day) => (
                              <div 
                                key={`${day.toISOString()}-${hour}`}
                                onContextMenu={(e) => {
                                    const d = new Date(day);
                                    d.setHours(hour);
                                    handleContextMenu(e, 'empty', d);
                                }}
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleDrop(e, day, hour)}
                                className={`orion-calendar-cell-hover-soft orion-calendar-cell-grid border-r border-slate-50 relative group transition-colors ${day.toDateString() === new Date().toDateString() ? 'bg-indigo-50/5' : ''}`}
                              >
                                {appointments.filter(a => {
                                  const date = new Date(a.start);
                                  return date.getHours() === hour && 
                                         date.getDate() === day.getDate() && 
                                         date.getMonth() === day.getMonth() &&
                                         date.getFullYear() === day.getFullYear();
                                }).map(appt => {
                                    const minutes = new Date(appt.start).getMinutes();
                                    const topPct = (minutes / 60) * 100;
                                    const durationMs = new Date(appt.end).getTime() - new Date(appt.start).getTime();
                                    const heightPx = (durationMs / 3600000) * 96;
                                    const weekCompactClass = view === 'week'
                                      ? 'p-1 text-[8px] rounded-md'
                                      : 'p-2 text-[10px] rounded-xl';
                                    const horizontalInsetClass = view === 'week'
                                      ? 'left-1/2 -translate-x-1/2 w-[78%]'
                                      : 'inset-x-1';
                                    const renderedHeight = view === 'week'
                                      ? Math.max(26, Math.min(heightPx * 0.58, 52))
                                      : heightPx;
                                    const topStyle = view === 'week'
                                      ? '50%'
                                      : `${topPct}%`;
                                    const translateYClass = view === 'week' ? '-translate-y-1/2' : '';

                                    return (
                                      <div 
                                        key={appt.id} 
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, appt)}
                                        onClick={(e) => { e.stopPropagation(); openEditModal(appt); }}
                                        onContextMenu={(e) => handleContextMenu(e, 'appointment', appt)}
                                        style={{ top: topStyle, height: `${renderedHeight}px` }}
                                        className={`absolute ${horizontalInsetClass} ${translateYClass} border font-bold shadow-sm cursor-pointer hover:scale-[1.02] active:cursor-grabbing transition-transform z-20 group/appt flex flex-col justify-start overflow-hidden ${weekCompactClass} ${getApptColor(appt.type)}`}
                                      >
                                        <div className="flex justify-between items-start gap-1">
                                            <span className="truncate leading-tight">{appt.title}</span>
                                            {appt.description && <span title="Note présente" className="text-[8px]">📝</span>}
                                        </div>
                                        <div className="opacity-70 mt-0.5 truncate text-[9px]">
                                            {leads.find(l => l.id === appt.leadId)?.name}
                                        </div>
                                        <div className="opacity-50 mt-auto text-[8px]">
                                            {new Date(appt.start).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - {new Date(appt.end).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                        </div>
                                      </div>
                                    );
                                })}
                              </div>
                            ))}
                          </div>
                        ))}
                     </div>
                )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CalendarView;
